import React, { useContext } from 'react';
import { FileSignature, Hourglass, IdCard, Target } from 'lucide-react';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import { CardAction } from '../ui/card-action';
import { AccionPendiente } from '../../utils/traspasos/tarjetasDeTraspaso';
import ProjectConfig from '../../constants';

export interface TarjetaAccionPendienteProps {
	/** Lo que decide `accionPendienteDelTraspaso`: una sola acción, la que bloquea el trámite. */
	accion: AccionPendiente;
	onVerificarIdentidad: () => void;
	onElegirDestino: () => void;
	/** Recibe el traspaso cuyo boletín se firma. */
	onFirmar: (transferId: string) => void;
}

const ICONO = 'w-5 h-5 text-warning';

/**
 * La acción que bloquea el traspaso, en la tarjeta de Proa (`ui/card-action`): identidad, destino
 * o firma (chirigota#586).
 *
 * 🔴 Identidad en revisión o rechazada va en modo `informative`: sin botón. `startKyc` no
 * comprueba el estado, así que cada clic era un `kyc-start` falso en el embudo B2C (30/09/26). En
 * rechazado se da la salida, y PULSABLE: comparsa no manda correo de rechazo y esta tarjeta es el
 * único sitio donde la persona se entera.
 */
const TarjetaAccionPendiente: React.FC<TarjetaAccionPendienteProps> = ({
	accion,
	onVerificarIdentidad,
	onElegirDestino,
	onFirmar
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);

	if (accion.tipo === 'destino') {
		return (
			<CardAction
				icon={<Target className={ICONO} strokeWidth={2} />}
				title={translate('individual.verifyDestination.title')}
				description={translate('individual.verifyDestination.description')}
				ctaLabel={translate('individual.verifyDestination.cta')}
				ctaTimeLabel={translate('individual.verifyDestination.badge')}
				onVerify={onElegirDestino}
			/>
		);
	}

	if (accion.tipo === 'firma') {
		return (
			<CardAction
				icon={<FileSignature className={ICONO} strokeWidth={2} />}
				title={translate('individual.transferSignature.title')}
				description={translate('individual.transferSignature.description')}
				ctaLabel={translate('individual.transferSignature.cta')}
				ctaTimeLabel={translate('individual.transferSignature.badge')}
				onVerify={() => onFirmar(accion.transferId)}
			/>
		);
	}

	if (accion.estado === 'pendiente') {
		return (
			<CardAction
				title={translate('individual.identityGate.title')}
				description={translate('individual.identityGate.description')}
				ctaLabel={translate('individual.identityGate.cta')}
				ctaTimeLabel={translate('individual.identityGate.badge')}
				onVerify={onVerificarIdentidad}
			/>
		);
	}

	const prefijo = accion.estado === 'rechazado' ? 'rejected' : 'review';
	return (
		<CardAction
			informative
			// El reloj solo para la espera; en rechazado, el carnet: un icono de alarma dramatizaría algo
			// que se resuelve escribiendo un correo.
			icon={
				accion.estado === 'enRevision' ? (
					<Hourglass className={ICONO} strokeWidth={2} />
				) : (
					<IdCard className="w-6 h-6 text-warning" strokeWidth={2} />
				)
			}
			title={translate(`individual.identityGate.${prefijo}.title`)}
			planLabel={translate(`individual.identityGate.${prefijo}.badge`)}
			description={translate(`individual.identityGate.${prefijo}.description`)}
		>
			{accion.estado === 'rechazado' && (
				<p className="text-sm text-muted-foreground">
					{translate('individual.identityGate.rejected.contactLabel')}{' '}
					<a
						className="font-medium text-primary-text underline underline-offset-2"
						href={`mailto:${ProjectConfig.ARCA_CONTACT.EMAIL}`}
					>
						{ProjectConfig.ARCA_CONTACT.EMAIL}
					</a>
				</p>
			)}
		</CardAction>
	);
};

export default TarjetaAccionPendiente;
